import React, { useState, useEffect, useRef } from "react";
import { InlineMath } from "react-katex";
import SetKeyboard from "./SetKeyboard";
import { normalizePreviewLabel } from "./previewLabel";
import { numericMathExpression, previewMathExpression } from "./mathExpression";

/* ===== Stałe layoutu – jak w FractionInput ===== */
const INDENT = 36;               // px – kolumna na ikonkę + odstęp
const PANEL_WIDTH_DESKTOP = 250; // px – szerokość panelu (bez INDENT)

/* ========= Parsowanie wartości ========= */
/**
 * Named export: używane w wielu zadaniach i inputach.
 * Obsługuje m.in.:
 * - 7,5√2 / 7.5√2
 * - 14,4885√2
 * - √2, 15√2/2
 * - √(3/2)
 * - sqrt(2)
 * - \sqrt{2}
 * - ułamki 3/√5, 3√5/5
 * - liczby mieszane A B/C
 */
export function parseRootFractionInput(input) { return numericMathExpression(input); }
export function formatLatexPreview(input, label = "") {
  label = normalizePreviewLabel(label);
 const preview = previewMathExpression(input);
 return preview ? (label ? label + " = " + preview : preview) : null;
}

/* ========= Komponent ========= */
export default function RootFractionInput({
  value,
  onChange,
  disabled,
  label: inputLabel = "x",
  previewPrefix = "Podgląd:",
  previewFormatter,
  panelWidthDesktop = PANEL_WIDTH_DESKTOP,
  indentPx = INDENT,
}) {
  const label = normalizePreviewLabel(inputLabel);
  const pickString = (v) =>
    v && typeof v === "object" && "raw" in v ? v.raw ?? "" : v ?? "";

  const [local, setLocal] = useState(pickString(value));
  const [kbdOpen, setKbdOpen] = useState(false);
  const inputRef = useRef(null);

  // sync z zewnątrz
  useEffect(() => {
    setLocal(pickString(value));
  }, [value]);

  // Emit only user edits; prop synchronization must never overwrite sibling fields.
  const updateLocal = (raw) => {
    setLocal(raw);
    onChange?.({ raw, parsed: parseRootFractionInput(raw) });
  };

  /* ===== utils kursora ===== */
  const setCaret = (pos) => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(pos, pos);
  };

  const insertAtCursor = (text) => {
    const el = inputRef.current;
    if (!el) return;
    const { selectionStart: s = 0, selectionEnd: e = 0 } = el;
    const next = local.slice(0, s) + text + local.slice(e);
    updateLocal(next);
    setTimeout(() => setCaret(s + String(text).length), 0);
  };

  const insertSqrt = () => insertAtCursor("√");

  /* ===== podgląd ===== */
  const previewCore = local ? (previewFormatter ? previewFormatter(local, label) : formatLatexPreview(local, label)) : null;
  const previewLatex = previewCore ? `\\displaystyle ${previewCore}` : null;

  /* ===== layout jak w FractionInput ===== */
  const containerStyle = {
    width: "100%",
    maxWidth: panelWidthDesktop + indentPx,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "flex-start",
  };

  const inputStyle = {
    width: `calc(100% - ${indentPx}px)`,
    minWidth: 0,
    flex: "0 0 auto",
  };

  return (
    <div style={containerStyle}>
      {/* wiersz: [ikona][ input + √ ] */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `${indentPx}px 1fr`,
          columnGap: 0,
          alignItems: "center",
          width: "100%",
        }}
      >
        <SetKeyboard
          render="toggle"
          targetRef={inputRef}
          open={kbdOpen}
          onOpenChange={setKbdOpen}
          onAction={(type, payload) => {
            if (type === "insert") insertAtCursor(String(payload ?? ""));
          }}
          compact
        />

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            width: "100%",
          }}
        >
          <input
            ref={inputRef}
            type="text"
            value={local}
            onChange={(e) => updateLocal(e.target.value)}
            disabled={disabled}
            placeholder="np. √2, 7,5√2, 3/√5, 3√5/5"
            className="input-secondary"
            style={inputStyle}
          />
          <button
            type="button"
            onClick={insertSqrt}
            disabled={disabled}
            className="btn-secondary"
            aria-label="Wstaw √"
            title="Wstaw √"
          >
            √
          </button>
        </div>
      </div>

      {/* podgląd */}
      {previewLatex && (
        <div className="font-semibold text-gray-200">
          {previewPrefix ? (
            <span>
              <strong>{previewPrefix}</strong>{" "}
              <InlineMath math={previewLatex} />
            </span>
          ) : (
            <InlineMath math={previewLatex} />
          )}
        </div>
      )}

      {/* panel klawiatury pod inputem */}
      {kbdOpen && (
        <div style={{ width: "100%" }}>
          <SetKeyboard
            render="panel"
            targetRef={inputRef}
            open={kbdOpen}
            onOpenChange={setKbdOpen}
            onAction={(type, payload) => {
              const el = inputRef.current;
              if (!el) return;
              const s = el.selectionStart ?? 0;
              const e = el.selectionEnd ?? 0;

              if (type === "insert") {
                insertAtCursor(String(payload ?? ""));
                return;
              }
              if (type === "clear") { updateLocal(""); setTimeout(() => setCaret(0), 0); return; }
              if (type === "moveLeft" || type === "moveRight") { setCaret(type === "moveLeft" ? Math.max(0, s - 1) : Math.min(local.length, e + 1)); return; }
              if (type === "backspace") {
                if (s !== e) {
                  const next = local.slice(0, s) + local.slice(e);
                  updateLocal(next);
                  setTimeout(() => setCaret(s), 0);
                } else if (s > 0) {
                  const next = local.slice(0, s - 1) + local.slice(e);
                  updateLocal(next);
                  setTimeout(() => setCaret(s - 1), 0);
                }
              }
            }}
            compact
            panelIndent={indentPx}
          />
        </div>
      )}
    </div>
  );
}
